import { Fragment, useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCareer } from '@components/icons/career';
import { IconCloseLarge } from '@components/icons/close-large';
import { IconCompany } from '@components/icons/company';
import { IconDropside } from '@components/icons/dropside';
import { IconFifty } from '@components/icons/fifty';
import { IconHamburgerMenu } from '@components/icons/hamburger-menu';
import { IconHelp } from '@components/icons/help';
import { IconNotifications } from '@components/icons/notifications';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconSignout } from '@components/icons/signout';
import { Nav } from '@components/nav';
import { HireNavStyleContainer } from '@components/nav/nav';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';

import { config } from 'app-config';

import { useNotificationCounts } from 'api/notifications/hooks';
import { useAuthenticatedUser, useSignOut, useSwitchCompany } from 'features/authentication';
import { usePermissions } from 'features/permissions';
import { useNavigationItems } from 'hooks/use-navigation-items';
import { useProfileNavigationItems } from 'hooks/use-profile-navigation-items';
import { useCompanySettingsNavigationItems } from 'hooks/use-settings-navigation-items';
import { useStore } from 'models/Provider';

type State = {
  isOpen: boolean;
  view: 'base' | 'companies' | 'profile' | 'settings';
};

export const MobileHeader = observer(({ title }: { title: string }) => {
  const location = useLocation();
  const inSettings = location.pathname.startsWith('/settings');
  const inProfile = location.pathname.startsWith('/profile');
  const view = inSettings ? 'settings' : inProfile ? 'profile' : 'base';
  const [state, setState] = useState<State>({
    isOpen: false,
    view: view,
  });

  const toggleOpen = useCallback(() => {
    setState((old) => ({
      isOpen: !old.isOpen,
      view: old.view,
    }));
  }, []);

  const showCompanies = useCallback(() => {
    setState((old) => ({ ...old, view: 'companies' }));
  }, []);

  const showSettings = useCallback(() => {
    setState((old) => ({ ...old, view: 'settings' }));
  }, []);

  const showProfile = useCallback(() => {
    setState((old) => ({ ...old, view: 'profile' }));
  }, []);

  const showMenu = useCallback(() => {
    setState((old) => ({ ...old, view: 'base' }));
  }, []);

  return (
    <HireNavStyleContainer>
      <Container>
        <Stack space={4} orientation="horizontal" align="center">
          <Link to="/">
            <IconFifty size={32} />
          </Link>
          <Text font="bold18" color="purple100" text={title} />
        </Stack>

        <Actions onOpenToggle={toggleOpen} isOpen={state.isOpen} />
      </Container>
      <AnimatePresence>
        {state.isOpen && (
          <NavContainer
            initial={{ opacity: 1, x: '100%' }}
            exit={{ opacity: 1, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Nav variant="cover">
              <AnimatePresence initial={false}>
                {state.view === 'base' && (
                  <MainMenu
                    onCompaniesClick={showCompanies}
                    onCompanySettingsClick={showSettings}
                    onProfileClick={showProfile}
                  />
                )}
              </AnimatePresence>

              <AnimatePresence>
                {state.view === 'companies' && <CompaniesList goBack={showMenu} />}
              </AnimatePresence>
              <AnimatePresence>
                {state.view === 'settings' && <CompanySettings goBack={showMenu} />}
              </AnimatePresence>
              <AnimatePresence>
                {state.view === 'profile' && <Profile goBack={showMenu} />}
              </AnimatePresence>
            </Nav>
          </NavContainer>
        )}
      </AnimatePresence>
    </HireNavStyleContainer>
  );
});

const MainMenu = ({
  onCompaniesClick,
  onCompanySettingsClick,
  onProfileClick,
}: {
  onCompaniesClick(): void;
  onCompanySettingsClick(): void;
  onProfileClick(): void;
}) => {
  const items = useNavigationItems();
  const user = useAuthenticatedUser();
  const signOut = useSignOut();
  const { t } = useStore();
  const { hasRolePermission } = usePermissions();

  return (
    <AnimatedContainer
      initial={{ opacity: 0, x: '-100%' }}
      exit={{ opacity: 0, x: '-100%' }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Nav.Group>
        {items.map((item) => (
          <Nav.Link key={item.path} href={item.path} label={item.label} iconLeft={item.icon} />
        ))}
      </Nav.Group>

      <Nav.Separator />

      {user.companies.length > 1 && (
        <Fragment>
          <Nav.Button
            iconLeft={<IconCompany />}
            iconRight={<IconDropside />}
            label={user.company.displayName}
            onClick={onCompaniesClick}
          />

          <Nav.Separator />
        </Fragment>
      )}

      <Nav.Button
        iconLeft={<Avatar color="purple" name={user.name} src={user.picture} />}
        onClick={onProfileClick}
        label={
          <div>
            <Text
              font="semibold14"
              text={`${user.name} - ${t(`user.role.${user.role}`)}`}
              color="white100"
            />
          </div>
        }
      />

      {hasRolePermission('company:view-settings') && (
        <Nav.Button
          onClick={onCompanySettingsClick}
          iconLeft={<IconCompany />}
          iconRight={<IconDropside />}
          label={t('usermenu.company.settings')}
        />
      )}

      <Nav.ExternalLink
        href={`${config.CAREERS_URL}/${user.company.name}`}
        label={t('navigation.careersite')}
        iconLeft={<IconCareer />}
        iconRight={<IconOpenNewTab />}
      />

      <Nav.Separator />

      <Nav.ExternalLink
        href="https://help.50skills.com/"
        iconLeft={<IconHelp />}
        label={t('usermenu.help')}
      />

      <Nav.Button
        onClick={() => signOut.mutate()}
        iconLeft={<IconSignout />}
        label={t('usermenu.logout')}
      />
    </AnimatedContainer>
  );
};

const Actions = observer<{ isOpen: boolean; onOpenToggle(): void }>(({ isOpen, onOpenToggle }) => {
  const { hasRolePermission } = usePermissions();
  const notificationsCountQuery = useNotificationCounts();
  const unread = notificationsCountQuery.data?.unread ?? 0;

  return (
    <Stack space={2} orientation="horizontal" align="center">
      {hasRolePermission('notifications:view') && (
        <div style={{ position: 'relative' }}>
          <Link to="/notifications">
            <IconButton variant="ghost-purple" icon={<IconNotifications />} />
          </Link>
          {unread > 0 && <UnreadNotifications>{unread > 99 ? '99+' : unread}</UnreadNotifications>}
        </div>
      )}

      <IconButton
        variant="ghost-purple"
        onClick={onOpenToggle}
        icon={isOpen ? <IconCloseLarge /> : <IconHamburgerMenu />}
      />
    </Stack>
  );
});

const CompaniesList = ({ goBack }: { goBack(): void }) => {
  const { company, companies } = useAuthenticatedUser();
  const switchCompany = useSwitchCompany();

  return (
    <AnimatedContainer
      initial={{ opacity: 0, x: '100%' }}
      exit={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Nav.Button heading iconLeft={<IconArrowLeft />} label="companies" onClick={goBack} />

      <Nav.Separator />

      {companies.map((c) => {
        const isSelected = c.id === company.id;

        return (
          <Nav.Button
            key={c.id}
            isSelected={isSelected}
            iconLeft={<IconCompany />}
            label={c.displayName}
            onClick={isSelected ? () => null : () => switchCompany.mutate(c)}
          />
        );
      })}
    </AnimatedContainer>
  );
};

const CompanySettings = ({ goBack }: { goBack(): void }) => {
  const items = useCompanySettingsNavigationItems();
  const { t } = useStore();

  return (
    <AnimatedContainer
      initial={{ opacity: 0, x: '100%' }}
      exit={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Nav.Button
        heading
        iconLeft={<IconArrowLeft />}
        label={t('navigation.back')}
        onClick={goBack}
      />

      <Nav.Separator />

      {items.map((item) => (
        <Nav.Link key={item.path} href={item.path} label={item.label} iconLeft={item.icon} />
      ))}
    </AnimatedContainer>
  );
};

const Profile = ({ goBack }: { goBack(): void }) => {
  const items = useProfileNavigationItems();
  const { t } = useStore();

  return (
    <AnimatedContainer
      initial={{ opacity: 0, x: '100%' }}
      exit={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Nav.Button
        heading
        iconLeft={<IconArrowLeft />}
        label={t('navigation.back')}
        onClick={goBack}
      />

      <Nav.Separator />

      {items.map((item) => (
        <Nav.Link key={item.path} href={item.path} label={item.label} iconLeft={item.icon} />
      ))}
    </AnimatedContainer>
  );
};

const NavContainer = styled(motion.div)({
  zIndex: 90,
  backgroundColor: theme.color.purple10,
  position: 'fixed',
  top: '4.5rem',
  left: 0,
  right: 0,
  bottom: 0,
});

const AnimatedContainer = styled(motion.div)({
  position: 'absolute',
  padding: '1rem',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
});

const Container = styled.div({
  zIndex: 100,
  position: 'relative',
  padding: '1rem',
  height: '4.5rem',
  width: '100%',
  color: theme.color.purple100,
  backgroundColor: theme.color.purple10,
  borderBottom: '0.0625rem solid ' + theme.color.purpleTrans10,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const UnreadNotifications = styled.div({
  position: 'absolute',
  zIndex: 10,
  backgroundColor: theme.color.purple100,
  color: theme.color.white100,
  font: theme.font.semibold10,
  borderRadius: '0.125rem',
  padding: '0.2rem',
  top: '0.2rem',
  left: '0.2rem',
  minWidth: '1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});
